import DOMPurify from "dompurify";
import { Clock, X } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { STORAGE, STORAGE_KEY } from "@/constants";
import { useChatInfo } from "@/hooks/use-csr-chat";
import { getItem, setItem } from "@/lib/storage-utils";
import {
  formatHour,
  getTimezoneAbbr,
  isOfflineNow,
  renderTemplate,
} from "@/lib/utils";
import type { ChatLiabilityData, ClientDetails } from "@/model/csr-chat";

const isProper = (value: unknown): value is ChatLiabilityData => {
  if (!value || typeof value !== "object") return false;
  const d = value as Partial<ChatLiabilityData>;
  if (
    typeof d.chat_liability_alert !== "string" ||
    !d.chat_liability_alert.trim()
  ) {
    return false;
  }
  const cd = d.client_details as Partial<ClientDetails> | undefined;
  if (!cd) return false;
  return (
    typeof cd.from_hour === "string" &&
    typeof cd.to_hour === "string" &&
    typeof cd.time_zone === "string"
  );
};

export const ChatLiabilityAlert = () => {
  const { data } = useChatInfo();
  const [dismissed, setDismissed] = useState(() =>
    Boolean(
      getItem(STORAGE_KEY.CHAT_LIABILITY_ALERT_DISMISSED, STORAGE.SESSION),
    ),
  );

  const handleDismiss = () => {
    setItem(STORAGE_KEY.CHAT_LIABILITY_ALERT_DISMISSED, true, STORAGE.SESSION);
    setDismissed(true);
  };

  if (dismissed) return null;
  if (!isProper(data)) return null;
  if (!isOfflineNow(data.client_details)) return null;

  const rendered = renderTemplate(data.chat_liability_alert, {
    from_hour: formatHour(data.client_details.from_hour),
    to_hour: formatHour(data.client_details.to_hour),
    time_zone: getTimezoneAbbr(data.client_details.time_zone),
  });

  const sanitized = DOMPurify.sanitize(rendered, {
    ALLOWED_TAGS: ["b", "strong", "em", "i", "u", "br", "span"],
    ALLOWED_ATTR: [],
  });

  return (
    <div className="absolute left-1 right-4 top-0 z-10 mx-3 mt-3 flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 shadow-sm">
      <Clock className="mt-0.5 size-4 shrink-0 text-amber-600" />
      <div
        className="flex-1 text-sm leading-snug text-amber-900"
        dangerouslySetInnerHTML={{ __html: sanitized }}
      />
      <Button
        variant="ghost"
        size="sm"
        onClick={handleDismiss}
        className="h-7 shrink-0 px-2 text-amber-900 hover:bg-amber-100"
        aria-label="Dismiss"
        disableLogging
      >
        Dismiss
        <X className="ml-1 size-3.5" />
      </Button>
    </div>
  );
};
